import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Button, Alert, Form } from 'react-bootstrap';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash, Search, Plus, PencilSquare } from 'react-bootstrap-icons';
import { shiftTemplateAPI } from '../../services';
import TaskFormModal from './TaskFormModal';
import { useBranchAPI } from '../../hooks';
import { toast } from 'react-toastify';

// Sortable Task Item Component (Available Tasks)
function SortableTaskItem({ task, onToggle, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `available-${task.id}`,
    data: {
      type: 'available',
      taskId: task.id,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`d-flex align-items-center gap-2 p-2 border rounded mb-2 ${isDragging ? 'shadow' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <GripVertical className="text-muted" />
      </div>
      <Form.Check
        type="checkbox"
        id={`task-${task.id}`}
        checked={false}
        onChange={(e) => onToggle(task.id, e.target.checked)}
        label={
          <span>
            {task.name}
            {task.is_urgent && (
              <span className="badge bg-danger ms-1" style={{ fontSize: '10px' }}>ĐỘT XUẤT</span>
            )}
          </span>
        }
        className="flex-grow-1 mb-0"
      />
      <Button
        size="sm"
        variant="outline-secondary"
        className="p-0 px-1 flex-shrink-0"
        onClick={(e) => { e.stopPropagation(); onEdit(task); }}
        title="Sửa task"
      >
        <PencilSquare size={13} />
      </Button>
    </div>
  );
}

// Assigned Task Item Component (with order display)
function AssignedTaskItem({ task, orderNo, onRemove, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `assigned-${task.id}`,
    data: {
      type: 'assigned',
      taskId: task.id,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`d-flex align-items-center gap-2 p-2 border rounded mb-2 ${isDragging ? 'shadow' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <GripVertical className="text-muted" />
      </div>
      <div className="badge bg-primary me-2">{orderNo}</div>
      <div className="flex-grow-1">
        <strong>{task.name}</strong>
        {task.is_urgent && (
          <span className="badge bg-danger ms-1" style={{ fontSize: '10px' }}>ĐỘT XUẤT</span>
        )}
      </div>
      <Button
        size="sm"
        variant="outline-secondary"
        className="p-0 px-1"
        onClick={(e) => { e.stopPropagation(); onEdit(task); }}
        title="Sửa task"
      >
        <PencilSquare size={13} />
      </Button>
      <Button
        size="sm"
        variant="outline-danger"
        onClick={() => onRemove(task.id)}
      >
        <Trash size={14} />
      </Button>
    </div>
  );
}

export default function TaskAssignmentModal({ show, onHide, template, allTasks, onSave, onTasksUpdate }) {
  const { selectedBranch } = useBranchAPI();
  const [assignedTasks, setAssignedTasks] = useState([]); // Array of { task_id, order_no, task }
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignedSearchTerm, setAssignedSearchTerm] = useState('');
  // #175019 — chi nhánh chỉ chọn ở thanh trên đầu; modal không còn ô lọc riêng.
  const branchFilter = selectedBranch?.id || '';
  const [categoryFilter, setCategoryFilter] = useState(''); // Filter theo category
  const [editingTask, setEditingTask] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Initialize assigned tasks from template
  useEffect(() => {
    if (show && template) {
      const tasks = Array.isArray(template.tasks) ? template.tasks : [];
      const initialAssigned = tasks.map((t, index) => ({
        task_id: t.id || t.task_id,
        order_no: t.order_no !== undefined ? t.order_no : index,
        task: t,
      }));
      // Sort by order_no
      initialAssigned.sort((a, b) => a.order_no - b.order_no);
      setAssignedTasks(initialAssigned);
    } else {
      setAssignedTasks([]);
    }
    // Reset search and filters when modal opens/closes
    setSearchTerm('');
    setCategoryFilter('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, template]);

  // Filter function for search
  const filterTask = (task) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return task.name?.toLowerCase().includes(search) || task.category?.toLowerCase().includes(search);
  };

  // Get unique categories from allTasks
  const categories = React.useMemo(() => {
    const uniqueCategories = new Set();
    allTasks.forEach((task) => {
      if (task.category) {
        uniqueCategories.add(task.category);
      }
    });
    return Array.from(uniqueCategories).sort();
  }, [allTasks]);

  // Get available tasks (not assigned) with branch and category filter
  const availableTasks = allTasks
    .filter((task) => !assignedTasks.some((at) => at.task_id === task.id))
    .filter((task) => {
      // Filter by branch: show tasks for selected branch OR tasks for all branches (branch_id = null)
      if (branchFilter && task.branch_id !== branchFilter && task.branch_id) {
        return false;
      }
      // Filter by category
      if (categoryFilter && task.category !== categoryFilter) {
        return false;
      }
      return true;
    })
    .filter(filterTask);

  // Get assigned task objects
  const assignedTaskObjects = assignedTasks
    .map((at) => {
      const task = allTasks.find((t) => t.id === at.task_id);
      return task ? { ...at, task } : null;
    })
    .filter(Boolean)
    .filter((at) => {
      if (!assignedSearchTerm.trim()) return true;
      const s = assignedSearchTerm.toLowerCase();
      return at.task.name?.toLowerCase().includes(s) || at.task.category?.toLowerCase().includes(s);
    });

  const handleAddTask = (taskId) => {
    const task = allTasks.find((t) => t.id === taskId);
    if (!task) return;

    // Find max order_no and add 1
    const maxOrderNo = assignedTasks.length > 0 ? Math.max(...assignedTasks.map((at) => at.order_no)) : -1;

    setAssignedTasks([
      ...assignedTasks,
      {
        task_id: taskId,
        order_no: maxOrderNo + 1,
        task,
      },
    ]);
  };

  const handleRemoveTask = (taskId) => {
    setAssignedTasks(assignedTasks.filter((at) => at.task_id !== taskId));
  };

  const handleToggleTask = (taskId, checked) => {
    if (checked) {
      handleAddTask(taskId);
    } else {
      handleRemoveTask(taskId);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle drag within assigned tasks (reorder)
    if (activeData?.type === 'assigned' && overData?.type === 'assigned') {
      const activeTaskId = activeData.taskId;
      const overTaskId = overData.taskId;

      const oldIndex = assignedTasks.findIndex((at) => at.task_id === activeTaskId);
      const newIndex = assignedTasks.findIndex((at) => at.task_id === overTaskId);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newAssigned = arrayMove(assignedTasks, oldIndex, newIndex);
        // Update order_no based on new position
        const updated = newAssigned.map((item, index) => ({
          ...item,
          order_no: index,
        }));
        setAssignedTasks(updated);
      }
    }
    // Handle drag from available to assigned
    else if (activeData?.type === 'available' && overData?.type === 'assigned') {
      handleAddTask(activeData.taskId);
    }
    // Handle drag from available to assigned container (drop zone)
    else if (activeData?.type === 'available' && over.id === 'assigned-container') {
      handleAddTask(activeData.taskId);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleEditSaved = async () => {
    setShowEditModal(false);
    setEditingTask(null);
    if (onTasksUpdate) await onTasksUpdate();
  };

  const handleSave = async () => {
    if (!template) return;

    try {
      setLoading(true);
      const tasks = assignedTasks.map((at) => ({
        task_id: at.task_id,
        order_no: at.order_no,
      }));

      await shiftTemplateAPI.setTemplateTasks(template.id, tasks);
      toast.success('Cập nhật danh sách task thành công!');
      onSave?.();
      onHide();
    } catch (err) {
      console.error('Error setting template tasks:', err);
      toast.error('Không thể cập nhật danh sách task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>Gán task cho cấu hình ca: {template?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Search Input and Filters */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <div className="position-relative">
                <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  type="text"
                  placeholder="Tìm kiếm task theo tên hoặc danh mục..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-5"
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Tất cả danh mục</option>
                {categories.map((category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Row>
            {/* Available Tasks Column */}
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  Danh sách task có sẵn
                  {searchTerm && <small className="text-muted ms-2">({availableTasks.length})</small>}
                </h6>
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => { setEditingTask(null); setShowEditModal(true); }}
                  className="d-flex align-items-center gap-1"
                >
                  <Plus size={16} />
                </Button>
              </div>

              {availableTasks.length === 0 ? (
                <Alert variant="info">
                  {searchTerm ? 'Không tìm thấy task nào phù hợp' : 'Tất cả task đã được gán'}
                </Alert>
              ) : (
                <SortableContext
                  items={availableTasks.map((t) => `available-${t.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                    {availableTasks.map((task) => (
                      <SortableTaskItem
                        key={task.id}
                        task={task}
                        onToggle={handleToggleTask}
                        onEdit={handleEditTask}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </Col>

            {/* Assigned Tasks Column */}
            <Col md={6}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  Task đã gán ({assignedTasks.length}
                  {assignedSearchTerm && assignedTaskObjects.length !== assignedTasks.length && (
                    <span className="text-muted"> / {assignedTaskObjects.length} hiển thị</span>
                  )}
                  )
                </h6>
              </div>
              <div className="position-relative mb-3">
                <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  type="text"
                  placeholder="Tìm trong task đã gán..."
                  value={assignedSearchTerm}
                  onChange={(e) => setAssignedSearchTerm(e.target.value)}
                  className="ps-5"
                />
              </div>
              {assignedTaskObjects.length === 0 ? (
                <Alert variant="warning">
                  {assignedSearchTerm ? 'Không tìm thấy task nào phù hợp' : 'Chưa có task nào được gán'}
                </Alert>
              ) : (
                <SortableContext
                  items={assignedTasks.map((at) => `assigned-${at.task_id}`)}
                  id="assigned-container"
                  strategy={verticalListSortingStrategy}
                >
                  <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                    {assignedTaskObjects.map((at) => (
                      <AssignedTaskItem
                        key={at.task_id}
                        task={at.task}
                        orderNo={at.order_no}
                        onRemove={handleRemoveTask}
                        onEdit={handleEditTask}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </Col>
          </Row>
        </DndContext>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
        >
          Hủy
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </Modal.Footer>
    </Modal>

    <TaskFormModal
      show={showEditModal}
      onHide={() => { setShowEditModal(false); setEditingTask(null); }}
      task={editingTask}
      onSave={handleEditSaved}
    />
    </>
  );
}
